/**
 * MaxDiff helpers.
 *
 * These two functions live outside the component on purpose. Both decide *which items a
 * respondent sees*, and a mistake in either produces a screen that looks perfectly
 * normal while quietly destroying the balance the whole question type depends on —
 * there is no error to notice. The respondent app has no component test harness, so
 * keeping this logic in a plain module is what makes it testable at all.
 */

/**
 * Choose one prepared arrangement for a respondent.
 *
 * Derived from the respondent's own key rather than a shared counter, so it needs no
 * coordination and cannot race between concurrent respondents. It is also stable: a
 * respondent who leaves and resumes gets the same screens their earlier answers refer
 * to. Re-picking on resume would leave stored answers pointing at items no longer shown.
 *
 * @param {Array<Array<Array<number>>>} designVersions indexed [version][set][position]
 * @param {string} respondentKey stable identifier for this respondent
 * @returns {Array<Array<number>>|null} one arrangement, or null if there are none
 */
export function pickDesignVersion (designVersions, respondentKey) {
  if (!Array.isArray(designVersions) || designVersions.length === 0) return null
  if (!respondentKey) return designVersions[0]

  return designVersions[hashKey(respondentKey) % designVersions.length]
}

/**
 * FNV-1a.
 *
 * Chosen over a simple character sum because respondent keys are mostly uuids that
 * differ in only a few characters, and a weak hash would map many of them onto the same
 * value — unbalancing the study without any visible sign.
 *
 * @param {string|number} key
 * @returns {number} unsigned 32-bit
 */
function hashKey (key) {
  const text = String(key)
  let hash = 0x811c9dc5

  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }

  return hash >>> 0
}

/**
 * Order the items of one screen for display.
 *
 * Respondents favour whatever sits at the top, so showing every item in the same place
 * every time bends the results. Which items appear is the arrangement's business; where
 * they sit on the screen is this function's.
 *
 * The order must be **stable**: derived from the respondent and the screen, never from
 * chance. A respondent who steps back a screen and forward again has to find the items
 * where they left them — and an order that changed on each render would be the same
 * class of bug this whole area has just been repaired for, only smaller and harder to
 * notice.
 *
 * @param {Array<Object>} items the items the arrangement chose for this screen
 * @param {string|number} seed stable per respondent and screen
 * @returns {Array<Object>} the same items, in display order
 */
export function orderWithinScreen (items, seed) {
  if (!Array.isArray(items)) return []

  const out = [...items]
  let state = hashKey(seed) || 1

  // Fisher-Yates driven by a linear congruential generator, so the order is decided
  // once by the seed and is identical every time it is asked for.
  for (let i = out.length - 1; i > 0; i--) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    const j = state % (i + 1)
    const swap = out[i]
    out[i] = out[j]
    out[j] = swap
  }

  return out
}

/**
 * Resolve one screen's item indexes to sub questions.
 *
 * The numbers in design_versions are positions in the item list **ordered by `order`**,
 * which is how the builder counts them when it generates the arrangements. Resolving
 * them against any other ordering hands the respondent items that look perfectly normal
 * and are simply the wrong ones: no error is raised, the screen renders, and the balance
 * the arrangements exist to guarantee is quietly gone.
 *
 * That is not hypothetical. The array arriving from the server was reordered in transit,
 * and the caller had no way to tell. So this function no longer trusts the order it is
 * given — it sorts by `order` itself, because it is the one place that knows what the
 * indexes mean. Items with no `order` keep the order they came in, which leaves plain
 * arrays behaving as before.
 *
 * @param {Array<Object>} subQuestions the question's items, any order
 * @param {Array<number>} setIndexes zero-based positions for one screen
 * @returns {Array<Object>} the items for that screen, in the order the arrangement lists them
 */
export function resolveSet (subQuestions, setIndexes) {
  if (!Array.isArray(subQuestions) || !Array.isArray(setIndexes)) return []

  const canonical = [...subQuestions].sort(
    (a, b) => (Number(a && a.order) || 0) - (Number(b && b.order) || 0)
  )

  const seen = new Set()

  return setIndexes
    .map(index => canonical[index])
    .filter(item => {
      // An index can be stale — a researcher may have deleted an item after the
      // arrangements were prepared. Drop it rather than rendering an empty row.
      if (!item) return false
      if (seen.has(item)) return false

      seen.add(item)

      return true
    })
}

/**
 * Work out what one screen's answers become after a pick.
 *
 * Returns a value for **every** item on the screen, not only the two that were picked.
 * The blank ones are what make the score's denominator work: they record that an item
 * was shown and passed over, which is otherwise indistinguishable from never shown.
 *
 * @param {Array<string>} itemCodes the codes on this screen, in display order
 * @param {Object} current existing answers for this screen, keyed by item code
 * @param {'most'|'least'} side which column was clicked
 * @param {string} itemCode the item that was clicked
 * @returns {{values: Object, clashed: boolean}} new answers keyed by item code
 */
export function buildScreenChanges (itemCodes, current, side, itemCode) {
  const codes = Array.isArray(itemCodes) ? itemCodes : []
  const existing = current || {}
  const other = side === 'most' ? 'least' : 'most'

  const pickedOn = wanted => codes.find(code => existing[code] === wanted) || null

  const picks = {
    most: side === 'most' ? itemCode : pickedOn('most'),
    least: side === 'least' ? itemCode : pickedOn('least'),
  }

  // An item cannot be both. Clearing the other side is less surprising than refusing
  // the click, which just looks like the radio is broken.
  const clashed = pickedOn(other) === itemCode
  if (clashed) picks[other] = null

  const values = {}
  codes.forEach(code => {
    if (picks.most === code) values[code] = 'most'
    else if (picks.least === code) values[code] = 'least'
    else values[code] = ''
  })

  return { values, clashed }
}

/**
 * Has this screen been fully answered?
 *
 * Both columns must be picked. Only items actually on the screen count, so a stale
 * answer left over from an arrangement the respondent no longer has cannot make an
 * unanswered screen look finished.
 *
 * @param {Array<string>} itemCodes the codes on this screen
 * @param {Object} current answers for this screen, keyed by item code
 * @returns {boolean}
 */
export function isScreenAnswered (itemCodes, current) {
  if (!Array.isArray(itemCodes) || !current) return false

  const onScreen = itemCodes.map(code => current[code])

  return onScreen.includes('most') && onScreen.includes('least')
}

/**
 * Which column the mobile accordion should open after a pick.
 *
 * On mobile the two columns are separate cards, so picking in one should move the
 * respondent to the other rather than leaving them to go looking for it. It must not
 * move once both are answered, or changing a pick would bounce the respondent away
 * from the column they are working in.
 *
 * @param {Array<string>} itemCodes the codes on this screen
 * @param {Object} current answers for this screen, keyed by item code, after the pick
 * @param {'most'|'least'} side the column just picked in
 * @returns {'most'|'least'|null} the column to open, or null to stay put
 */
export function nextOpenColumn (itemCodes, current, side) {
  const codes = Array.isArray(itemCodes) ? itemCodes : []
  const existing = current || {}
  const other = side === 'most' ? 'least' : 'most'

  const otherIsAnswered = codes.some(code => existing[code] === other)

  return otherIsAnswered ? null : other
}

/**
 * Which MaxDiff question on the page should consume the survey's Next button.
 *
 * The survey's own Next drives movement between screens, so a page holding more than
 * one MaxDiff needs a deterministic answer to "which one moves". Taking the first that
 * still has screens ahead walks them in the order they appear, which is the order the
 * respondent reads them in.
 *
 * Returns null when every MaxDiff on the page is on its last screen — that is the
 * signal for Next to mean what it normally means and submit the page.
 *
 * @param {Array<{qid: number|string, index: number, total: number}>} screens in page order
 * @returns {number|string|null} qid to advance, or null
 */
export function pickAdvanceTarget (screens) {
  if (!Array.isArray(screens)) return null

  const target = screens.find(s => s && s.total > 1 && s.index < s.total - 1)

  return target ? target.qid : null
}

/**
 * Which MaxDiff question should consume the survey's Back button.
 *
 * The mirror of pickAdvanceTarget: the last question with a screen behind it, so going
 * back reverses the path forward exactly rather than jumping to a different question.
 *
 * Returns null when every MaxDiff on the page sits on its first screen, letting Back
 * mean what it normally means and leave the page.
 *
 * @param {Array<{qid: number|string, index: number, total: number}>} screens in page order
 * @returns {number|string|null} qid to move back, or null
 */
export function pickRetreatTarget (screens) {
  if (!Array.isArray(screens)) return null

  const candidates = screens.filter(s => s && s.index > 0)

  return candidates.length ? candidates[candidates.length - 1].qid : null
}

/**
 * Which screen a returning respondent should land on.
 *
 * Counting answered screens is not enough. A respondent who goes back and clears a pick
 * leaves a gap, and a count would point past it — at a screen already answered, while
 * the blank one sits behind them with nothing to lead them to it. They would then be
 * refused at submit for a screen they cannot find.
 *
 * So: the first screen still missing an answer, or the last screen if none are. Landing
 * on the last one matters as much as the rest — a respondent returning to a question
 * they already finished should be one press from moving on, not ten.
 *
 * @param {Array<Array<number>>} sets the respondent's arrangement
 * @param {Array<Object>} subQuestions the question's items, in stored order
 * @param {Object} value the survey's flat answer map
 * @param {number|string} qid
 * @returns {number} screen index to open on
 */
export function resumeScreenIndex (sets, subQuestions, value, qid) {
  if (!Array.isArray(sets) || !sets.length || !value) return 0

  for (let index = 0; index < sets.length; index++) {
    const items = resolveSet(subQuestions, sets[index])

    const current = {}
    items.forEach(item => {
      current[item.code] = value[[qid, item.code, index].join('_')] || ''
    })

    if (!isScreenAnswered(items.map(i => i.code), current)) return index
  }

  return sets.length - 1
}
